import React, { useEffect, useMemo, useState } from 'react';

import { WidgetsMapComponentKeys } from 'config/widgetConfigurations/widgetsConfigurations';
import { getStringWithoutHash } from 'utilitys/helpers/general';
import { addNarrativeRequest } from 'utilitys/narrative/narrativeTools';

import useCustomPageSubTitle from 'hooks/useCustomPageSubTitle';
import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';
import { useGlobalFiltersHook } from 'hooks/useGlobalFiltersHook';
import { useReactTableState } from 'hooks/useReactTableState';
import useTableCurrentState from 'hooks/useTableCurrentState';

import { useResourceAnalysisStore } from 'store/resourceAnalysis.store';
import { useVersionStore } from 'store/version.store';

import { useMutationCsvExportResourceTable } from 'api/mutations/csvExportResourceTable';
import { useMutationSetWidgetColumns } from 'api/mutations/setWidgetColumns.mutation';
import { useQueryGetProjectDatesForResourceChart } from 'api/queries/getProjectDatesForResourceChart.query';
import { QueryResponse, useQueryGetResourceTable } from 'api/queries/getResourceTable.query';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { getInputLabel } from 'components/common/CustomFilters/TableDateRangeFilter/TableDateRangeFilter.utils';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { FilterNames, getFiltersData } from 'components/common/GlobalFilterComponent/GlobalFilterComponent.utils';
import { rangeOptions } from 'components/common/GlobalFilterComponent/components/RangeFilter/rangeFilter';
import { quickAnswerFilterOptions } from 'components/common/GlobalFilterComponent/components/quickAnswerFilter/quickAnswerFilter';
import { resourceTypeOptions } from 'components/common/GlobalFilterComponent/components/resourceTypeFilter/resourceTypeFilter';
import NoData from 'components/common/NoData/noData';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { serializeColumnsVisibility } from 'components/common/Tables/Tables.utils';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import { SelectedRangeLabel } from 'components/common/selectedRangeLabel/selectedRangeLabel';

import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/style/table.module.scss';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { PinboardWidgetPills } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetPills/PinboardWidgetPills';
import { usePinboardWidgetWrapperContext } from 'components/Dashboards/Project/Components/CustomDashboard/PinboardWidgetWrapper/context/widgetWrapperContext';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { aggregationOptions } from 'components/widgets/ResourcesAnalysisChart/ResourcesAnalysisChart.consts';
import { columnsV8, initialState } from 'components/widgets/resourcesAnalysisTable/resourcesAnalysisTable.utils';

const title = 'Resources Analysis | Activities Drilldown';
const id = 'resourceAnalysisTable';
const componentKey: WidgetsMapComponentKeys = 'resourcesAnalysisTable';

export const ResourcesAnalysisTable = ({
    projectId,
    contractId,
    widgetId,
    latestVersionId,
    compareVersionId,
    filter,
    globalFilterData,
    externalComponents = null,
    editNarrative = null,
}: IComponentProps) => {
    const { isGenerativeAi, reportId } = usePinboardWidgetWrapperContext();
    const {
        pagination,
        setPagination,
        sorting,
        setSorting,
        columnVisibility,
        setColumnVisibility,
        columnOrder,
        setColumnOrder,
        rowSelection,
        setRowSelection,
    } = useReactTableState({ initialSorting: widgetId && filter?.filters ? filter?.filters.sortingRequestList : [] });
    const [modalColumnsOpen, setModalColumnsOpen] = useState<boolean>(false);
    const updatedInitialState = useTableCurrentState({ initialState, componentKey, widgetId });
    const [totalPages, setTotalPages] = useState(0);
    const { handleSnackBar } = useCustomSnackBar();
    const { version } = useVersionStore();
    const { update, selectedSeries, aggregation, selectedEndDate, selectedStartDate, selectedValue } =
        useResourceAnalysisStore();

    const filters = useMemo(
        () =>
            widgetId
                ? filter?.filters.mainFilters
                : getFiltersData({
                      [FilterNames.ACTIVITY_CODES]: globalFilterData?.activityCodes
                          ?.filter((item) => !item.value.isExclude)
                          .map((i) => i.value.name),
                      [FilterNames.ACTIVITY_CODES_EXCLUDE]: globalFilterData?.activityCodes
                          ?.filter((item) => item.value.isExclude)
                          .map((i) => i.value.name),
                      [FilterNames.FRAGNET_SEARCH]: globalFilterData?.milestoneFragnet,
                      [FilterNames.TAG_CARD_BOARD]: globalFilterData?.tags
                          ?.filter((item) => !item.value.isExclude)
                          .map((i) => i.value.name),
                      [FilterNames.TAG_CARD_BOARD_EXCLUDE]: globalFilterData?.tags
                          ?.filter((item) => item.value.isExclude)
                          .map((i) => i.value.name),
                      [FilterNames.WBS_SEARCH]: globalFilterData?.wbs,
                      [FilterNames.RESOURCE_NAME_CARD_BOARD]: globalFilterData?.resource,
                      [FilterNames.RESOURCE_TYPE_CARD_BOARD]: globalFilterData?.resourceType,
                  }),
        [filter, globalFilterData],
    );

    const { data: minMaxDates } = useQueryGetProjectDatesForResourceChart({
        lastMetaDataId: latestVersionId,
        comparedMetaDataId: compareVersionId,
    });

    const dateRangeFilterValue = useMemo(() => {
        return globalFilterData?.dateRangeFilter;
    }, [globalFilterData?.dateRangeFilter]);

    const { startDate, endDate } = useMemo(() => {
        const value = widgetId && filter ? filter?.filters.range : dateRangeFilterValue;
        if (value && minMaxDates) {
            if (Array.isArray(value)) {
                if (value[0] && value[1]) {
                    return {
                        startDate: value[0],
                        endDate: value[1],
                    };
                } else {
                    return {
                        startDate: minMaxDates?.startProjectDate,
                        endDate: minMaxDates?.endProjectDate,
                    };
                }
            }
        }
        return {
            startDate: null,
            endDate: null,
        };
    }, [widgetId, filter?.filters.range, dateRangeFilterValue, minMaxDates]);

    const range = useMemo(() => {
        const value = widgetId && filter ? filter?.filters.range : dateRangeFilterValue;
        return Array.isArray(value) ? 'R_2W' : value;
    }, [widgetId, filter, filter?.filters.range, dateRangeFilterValue]);

    const { data, isLoading, isFetching } = useQueryGetResourceTable({
        metaDataId: widgetId && filter ? filter?.filters.metaDataId : version ? version.id : latestVersionId,
        comparedMetaDataId: widgetId && filter ? filter?.filters.comparedMetaDataId : (compareVersionId ?? null),
        range,
        filter: {
            filters,
        },
        startDate,
        endDate,
        sortingRequestList: widgetId && filter ? filter?.filters.sortingRequestList : sorting,
        paging: { ...pagination, page: pagination.page - 1 },
        barId: widgetId && filter ? filter?.filters.barId : selectedValue,
        aggregation: widgetId && filter ? filter?.filters.aggregation : aggregation.value,
        quickAnswerFilter: widgetId && filter ? filter?.filters.quickAnswerFilter : globalFilterData?.quickAnswerFilter,
        ...addNarrativeRequest(isGenerativeAi, componentKey, widgetId, reportId),
    });

    const { generateFilters: formatedFilter } = useGlobalFiltersHook({
        widgetId,
        filter: widgetId && filter ? { filters: filter?.filters.mainFilters || [] } : undefined,
        latestVersionId,
    });

    const route = `${getStringWithoutHash(window.location.href)}#${id}`;

    const pinBoardFilterData = useMemo(() => {
        return {
            metaDataId: version ? version.id : latestVersionId,
            comparedMetaDataId: compareVersionId ?? null,
            sortingRequestList: sorting,
            aggregation: aggregation.value,
            resourceType: globalFilterData?.resourceType,
            quickAnswerFilter: globalFilterData?.quickAnswerFilter,
            range: globalFilterData?.dateRangeFilter,
            resourceId: globalFilterData?.resource,
            barId: selectedValue,
            mainFilters: filters,
        };
    }, [sorting, aggregation, globalFilterData, version, compareVersionId, selectedValue, filters]);

    const tableInstance = useMemo(() => {
        const hiddenColumns = Object.keys(columnVisibility).reduce((acc: string[], item: string) => {
            columnVisibility[item] === false && acc.push(item);
            return acc;
        }, []);

        return {
            setColumnOrder,
            setHiddenColumns: (hiddenColumns) =>
                setColumnVisibility(serializeColumnsVisibility({ columns: columnsV8, hiddenColumns })),
            state: {
                hiddenColumns,
            },
            allColumns: columnOrder
                .map((item) => {
                    const column = columnsV8.find((column) => column.id === item);
                    if (column && column.id) {
                        return {
                            ...column,
                            isVisible: !hiddenColumns.includes(column.id),
                        };
                    }
                    return null;
                })
                .filter((x) => x),
        };
    }, [columnVisibility, columnOrder]);

    const { mutate: mutateSetWidgetColumns } = useMutationSetWidgetColumns();
    const { mutate: mutateExportCsv, isLoading: exportCsvLoading } = useMutationCsvExportResourceTable();
    const { sendEventWithDimensions } = useEventWithDimensions();
    const subTitle = useCustomPageSubTitle({ projectId, contractId, latestVersionId });
    const setWidgetColumns = (widgetId: string) => {
        if (tableInstance) {
            mutateSetWidgetColumns(
                { columns: { hiddenColumns: tableInstance.state.hiddenColumns, columnOrder }, widgetId },
                {
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                },
            );
        }
    };

    const handleAPICsvExport = () => {
        if (latestVersionId) {
            mutateExportCsv(
                {
                    metaDataId:
                        widgetId && filter ? filter?.filters.metaDataId : version ? version.id : latestVersionId,
                    comparedMetaDataId:
                        widgetId && filter ? filter?.filters.comparedMetaDataId : (compareVersionId ?? null),
                    range,
                    filter: {
                        filters,
                    },
                    sortingRequestList: widgetId && filter ? filter?.filters.sortingRequestList : sorting,
                    paging: { ...pagination, page: pagination.page - 1 },
                    barId: widgetId && filter ? filter?.filters.barId : selectedValue,
                    aggregation: widgetId && filter ? filter?.filters.aggregation : aggregation.value,
                    quickAnswerFilter:
                        widgetId && filter ? filter?.filters.quickAnswerFilter : globalFilterData?.quickAnswerFilter,
                    fileName: `${title}_${subTitle}`,
                },
                {
                    onSuccess: () => {
                        handleSnackBar('File Successfully Downloaded', 'success');

                        sendEventWithDimensions({
                            category: 'Export',
                            action: 'CSV',
                            label: `${title} ${subTitle}`,
                        });
                    },
                    onError: (error) => {
                        DEFAULT_ERROR({ error, handleError: handleSnackBar });
                    },
                },
            );
        }
    };

    useEffect(() => {
        setPagination({ ...pagination, page: 1 });
    }, [
        sorting,
        globalFilterData?.resourceType,
        globalFilterData?.quickAnswerFilter,
        globalFilterData?.range,
        globalFilterData?.resource,
        selectedValue,
        aggregation.value,
        filters,
    ]);

    useEffect(() => {
        update({
            selectedValue: null,
            selectedSeries: null,
            selectedStartDate: null,
            selectedEndDate: null,
            dataPointIndex: null,
            seriesIndex: null,
        });
    }, [globalFilterData]);

    useEffect(() => {
        updatedInitialState &&
            setColumnOrder(updatedInitialState.columnOrder ?? columnsV8.map((item) => item.id as string));
        updatedInitialState &&
            setColumnVisibility(
                serializeColumnsVisibility({ columns: columnsV8, hiddenColumns: updatedInitialState.hiddenColumns }) ??
                    {},
            );
    }, [updatedInitialState]);

    useEffect(() => {
        if (data) {
            setTotalPages(data.numOfPages);
        }
    }, [data]);

    const filteredRows = useMemo(() => data?.cardList || [], [data]);

    const rangeFilterValue = useMemo(() => {
        if (widgetId && filter) {
            const value = widgetId && filter ? filter?.filters.range : null;
            if (Array.isArray(value)) {
                return [getInputLabel({ startDate: value[0], endDate: value[1] })];
            } else {
                return [rangeOptions.find((item) => item.value === value)?.label || ''];
            }
        }
        return [];
    }, [widgetId, filter, filter?.filters.range]);

    const allFormattedFilters = [
        ...formatedFilter,
        {
            name: 'Aggregation',
            data: aggregation.value ? [aggregationOptions.find((item) => item.value === aggregation.value)?.label] : [],
        },
        {
            name: 'Resource Type',
            data:
                widgetId && filter.filters?.resourceType
                    ? [resourceTypeOptions.find((item) => item.value === filter.filters.resourceType)?.label]
                    : [],
        },
        {
            name: 'Quick Answer Filter',
            data:
                widgetId && filter.filters?.quickAnswerFilter
                    ? [quickAnswerFilterOptions.find((item) => item.value === filter.filters.quickAnswerFilter)?.label]
                    : [],
        },
        {
            name: 'Date Range',
            data: rangeFilterValue,
        },
    ];

    return (
        <WidgetWithTitle
            id={id}
            title={<WidgetTitle title={title} postfix={data?.numOfAllCards ? `(${data?.numOfAllCards})` : undefined} />}
            titleFilters={[
                editNarrative && <div key={'editNarrative'}>{editNarrative}</div>,
                widgetId ? (
                    <PinboardWidgetPills key={'widgetFilters'} filters={allFormattedFilters} />
                ) : (
                    <div className={classes.filters} key={'selectedRangeLabel'}>
                        <SelectedRangeLabel
                            selectedEndDate={selectedEndDate}
                            selectedStartDate={selectedStartDate}
                            seriesLabel={selectedSeries}
                        />
                    </div>
                ),
            ]}
            titleComponents={[
                externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                <AddWidget
                    key={'AddWidget'}
                    componentKey={componentKey}
                    title={title}
                    projectId={projectId}
                    contractId={contractId}
                    widgetId={widgetId}
                    route={route}
                    filters={pinBoardFilterData}
                    callBack={setWidgetColumns}
                    shouldHide={true}
                />,
                <ExportDropdown
                    key={'export'}
                    title={title}
                    handleAPICsvExport={handleAPICsvExport}
                    subTitle={subTitle}
                />,
                <ColumnsManager
                    onOpenModal={() => setModalColumnsOpen(true)}
                    onHideModal={() => setModalColumnsOpen(false)}
                    showModal={modalColumnsOpen}
                    widgetId={widgetId}
                    componentKey={componentKey}
                    tableInstance={tableInstance}
                    initialState={initialState}
                    columns={columnsV8}
                    onChangeOrder={setColumnOrder}
                    onChangeVisibility={setColumnVisibility}
                />,
                <TooltipIcon key={'info'} tooltip={data?.info} />,
                <ThreeDotsMenu widgetId={widgetId} componentKey={componentKey} shareTitle={title} shareLink={route} />,
            ]}
        >
            <TableContainer>
                {updatedInitialState && (
                    <TableV8<QueryResponse['cardList'][0]>
                        data={filteredRows}
                        columns={columnsV8}
                        isLoading={isLoading || isFetching || exportCsvLoading}
                        noData={<NoData>No activities match the applied filter</NoData>}
                        sorting={sorting}
                        setSorting={setSorting}
                        columnVisibility={columnVisibility}
                        setColumnVisibility={setColumnVisibility}
                        columnOrder={columnOrder}
                        setColumnOrder={setColumnOrder}
                        rowSelection={rowSelection}
                        setRowSelection={setRowSelection}
                        pagination={pagination}
                        totalPages={totalPages}
                        setPagination={setPagination}
                        isPaginationDisabled={isFetching}
                        displayPaginationBottomBar
                    />
                )}
            </TableContainer>
        </WidgetWithTitle>
    );
};
